/**
 * Best efforts row on ride detail (CHARTS_PLAN §6): best mean power for
 * 5 s … 60 min from ``power_bests``, starred when it is the rider's best
 * of the last 90 days.
 */
import { useTranslation } from 'react-i18next';
import { usePowerCurve } from '../../hooks/useFitness.ts';

const EFFORT_SECONDS = [5, 60, 300, 1200, 3600] as const;
const STAR_RANGE_DAYS = 90;

export function BestEfforts({
  rideId,
  bests,
}: Readonly<{ rideId: number; bests: Record<string, number> | null | undefined }>) {
  const { t } = useTranslation();
  const efforts = EFFORT_SECONDS.flatMap((sec) => {
    const watts = bests?.[String(sec)];
    return watts == null ? [] : [{ sec, watts }];
  });
  const { data: curve } = usePowerCurve(STAR_RANGE_DAYS, false, efforts.length > 0);
  if (efforts.length === 0) return null;

  const topRide = new Map((curve?.best ?? []).map((p) => [p.sec, p.ride_id]));
  return (
    <section
      aria-labelledby="best-efforts-title"
      className="rounded-lg bg-(--color-bg-secondary) border border-(--color-border) p-4 flex flex-col gap-2"
    >
      <h3 id="best-efforts-title" className="text-sm font-medium text-(--color-text-secondary)">
        {t('charts.efforts.title')}
      </h3>
      <ul className="grid grid-cols-3 sm:grid-cols-5 gap-2">
        {efforts.map(({ sec, watts }) => (
          <li
            key={sec}
            className="rounded-lg bg-(--color-bg-tertiary) border border-(--color-border) p-2 text-center"
          >
            <div className="text-xs text-(--color-text-secondary)">
              {sec < 60 ? t('charts.secLong', { n: sec }) : t('charts.minLong', { n: sec / 60 })}
            </div>
            <div className="text-base font-semibold text-(--color-text-primary)">
              {Math.round(watts)} W
              {topRide.get(sec) === rideId && (
                <span
                  role="img"
                  aria-label={t('charts.efforts.best90')}
                  title={t('charts.efforts.best90')}
                  className="ml-1 text-(--chart-power)"
                >
                  ★
                </span>
              )}
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
}
